import { ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'src/components/ui/badge';
import { ProductImage } from 'src/features/novelties/components/ProductImage';
import { SalesPotentialEditor } from 'src/features/novelties/components/SalesPotentialEditor';
import { cn } from 'src/lib/utils';
import type { NoveltyProductData } from 'src/services/api/getNoveltiesBestCandidates';

function formatPrice(
  price: number | null,
  currency: string | null,
  locale: string,
): string | null {
  if (price === null || !currency || currency === 'NON DEFINI') {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(price);
}

type ProductCardHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  product: NoveltyProductData;
  isExpanded: boolean;
  className?: string;
};

export const ProductCardHeader = ({
  product,
  isExpanded,
  className,
  ref,
  ...props
}: ProductCardHeaderProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const { t, i18n } = useTranslation('novelties');

  const formattedPurchasePrice = formatPrice(
    product.purchase_price,
    product.purchase_currency,
    i18n.language,
  );

  return (
    <div
      className={cn(
        'flex w-full cursor-pointer items-center justify-between p-4 text-left transition-colors hover:bg-layout-hover',
        isExpanded && 'border-border-default border-b bg-layout-hover',
        className,
      )}
      ref={ref}
      {...props}
    >
      <div className='flex items-center gap-4'>
        <div className='flex size-10 items-center justify-center rounded-lg bg-primary-default/10'>
          {isExpanded ? (
            <ChevronDown className='size-5 text-primary-default' />
          ) : (
            <ChevronRight className='size-5 text-primary-default' />
          )}
        </div>
        <ProductImage
          alt={product.title || product.sku}
          size='md'
          src={product.image_url}
        />
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <span className='font-mono font-semibold text-sm'>
              {product.sku}
            </span>
            {product.similarities.length > 0 && (
              <Badge className='text-xs' variant='secondary'>
                {product.similarities.length}{' '}
                {product.similarities.length === 1
                  ? t('candidate')
                  : t('candidates')}
              </Badge>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {product.title && (
              <span className='max-w-md truncate text-layout-foreground-secondary text-sm'>
                {product.title}
              </span>
            )}
            {formattedPurchasePrice && (
              <Badge
                className='flex items-center gap-1 text-xs'
                variant='outline'
              >
                <span className='font-normal text-layout-foreground-secondary'>
                  {t('Purchase Price')}:
                </span>
                <span className='font-medium font-mono'>
                  {formattedPurchasePrice}
                </span>
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center gap-4'>
        <SalesPotentialEditor
          salesPotential={product.sales_potential}
          sku={product.sku}
        />
      </div>
    </div>
  );
};

ProductCardHeader.displayName = 'ProductCardHeader';
